import {
    addDays,
    addMonths,
    addQuarters,
    addYears,
    format,
    startOfMonth,
    startOfQuarter,
    startOfYear,
} from "date-fns";
import { endOfMonth, endOfQuarter, endOfYear } from "date-fns/esm";
import { useState } from "react";
import { useStateParams } from "../../hooks/useStateParams";
import { DateFilterValue } from "../../constants";

const quarterLabel = ["Jan - Mar", "Apr - Jun", "Jul - Sep", "Oct - Dec"];

const defaultDateFilterValue: DateFilterValue = "this-year";

const currentDate = new Date();
const currentYear = currentDate.getFullYear();

const qaurter1Index = 0;
const quarter4Index = 3;

const getQuarterLabelFromDate = (
    date: Date,
    quarterOffset?: number
): string => {
    let quarterYear = date.getFullYear();
    let quarterIndex = Math.ceil((currentDate.getMonth() + 1) / 3) - 1;

    if (quarterOffset) {
        quarterIndex = quarterIndex + quarterOffset;

        if (quarterIndex === -1) {
            // We have went back to previous year
            quarterIndex = quarter4Index;
            quarterYear = quarterYear - 1;
        }

        if (quarterIndex === 4) {
            // we have went forward to following year
            quarterIndex = qaurter1Index;
            quarterYear = quarterYear + 1;
        }
    }

    return `${quarterLabel[quarterIndex]} ${quarterYear}`;
};

const selectTextMap: Record<DateFilterValue, string> = {
    "this-month": format(currentDate, "MMMM yyyy"),
    "this-quarter": getQuarterLabelFromDate(currentDate),
    "this-year": `Jan ${currentYear} - Dec ${currentYear}`,
    "last-month": format(addMonths(currentDate, -1), "MMMM yyyy"),
    "last-quarter": getQuarterLabelFromDate(currentDate, -1),
    "last-year": `Jan ${currentYear - 1} - Dec ${currentYear - 1}`,
    "two-years-ago": `Jan ${currentYear - 2} - Dec ${currentYear - 2}`,
    "three-years-ago": `Jan ${currentYear - 3} - Dec ${currentYear - 3}`,
    "four-years-ago": `Jan ${currentYear - 4} - Dec ${currentYear - 4}`,
    "five-years-ago": `Jan ${currentYear - 5} - Dec ${currentYear - 5}`,
    all: "All",
};

export const startEndDateMap: Record<DateFilterValue, [Date, Date]> = {
    "this-month": [startOfMonth(currentDate), endOfMonth(currentDate)],
    "this-quarter": [startOfQuarter(currentDate), endOfQuarter(currentDate)],
    "this-year": [startOfYear(currentDate), endOfYear(currentDate)],
    "last-month": [
        startOfMonth(addMonths(currentDate, -1)),
        endOfMonth(addMonths(currentDate, -1)),
    ],
    "last-quarter": [
        startOfQuarter(addQuarters(currentDate, -1)),
        endOfQuarter(addQuarters(currentDate, -1)),
    ],
    "last-year": [
        startOfYear(addYears(currentDate, -1)),
        endOfYear(addYears(currentDate, -1)),
    ],
    "two-years-ago": [
        startOfYear(addYears(currentDate, -2)),
        endOfYear(addYears(currentDate, -2)),
    ],
    "three-years-ago": [
        startOfYear(addYears(currentDate, -3)),
        endOfYear(addYears(currentDate, -3)),
    ],
    "four-years-ago": [
        startOfYear(addYears(currentDate, -4)),
        endOfYear(addYears(currentDate, -4)),
    ],
    "five-years-ago": [
        startOfYear(addYears(currentDate, -5)),
        endOfYear(addYears(currentDate, -5)),
    ],
    all: [new Date(new Date(1, 1).setFullYear(0)), addDays(new Date(), 1)],
};

const isOfDateFilterValueType = (value: string): value is DateFilterValue => {
    return [
        "this-month",
        "this-quarter",
        "this-year",
        "last-month",
        "last-quarter",
        "last-year",
        "two-years-ago",
        "three-years-ago",
        "four-years-ago",
        "five-years-ago",
        "all",
    ].includes(value);
};

export const useDateRangeFilterDropdown = () => {
    const [value, setValue] = useStateParams<DateFilterValue>(
        defaultDateFilterValue,
        "dateRange",
        (s) => s,
        (s) => (isOfDateFilterValueType(s) ? s : defaultDateFilterValue),
        true
    );
    const [label, setLabel] = useState(selectTextMap[value]);
    const [startEndDate, setStartEndDate] = useState(startEndDateMap[value]);

    const handleChange = (value: DateFilterValue) => {
        setValue(value);
        setLabel(selectTextMap[value]);
        setStartEndDate(startEndDateMap[value]);
    };

    return {
        value,
        label,
        startDate: format(startEndDate[0], "yyyy-MM-dd"),
        endDate: format(startEndDate[1], "yyyy-MM-dd"),
        handleChange,
    };
};
